import React, { useState } from "react";

const BalanceTransferOptions = ({ formData, setFormData, setCurrentStep }) => {
  // Local state mapped to formData if user comes back to this step
  const [occupation, setOccupation] = useState(formData?.professionType || "salaried");
  const [income, setIncome] = useState(formData?.monthly_income || "");
  const [loanRequirement, setLoanRequirement] = useState(formData?.loan_requirement || "transfer_topup");

  // Format income with commas for Indian Rupee system (e.g., 50000 -> 50,000)
  const handleIncomeChange = (e) => {
    const rawValue = e.target.value.replace(/\D/g, ""); // Remove non-digits
    if (rawValue) {
      const formattedValue = Number(rawValue).toLocaleString("en-IN");
      setIncome(formattedValue);
    } else {
      setIncome("");
    }
  };

  const handleContinue = () => {
    // Save to parent state before proceeding
    setFormData((prev) => ({
      ...prev,
      professionType: occupation,
      monthly_income: income.replace(/\D/g, ""), // save raw number to DB
      loan_requirement: loanRequirement,
    }));
    
    // Proceed to Offer Page (Step 5 or 6 depending on your routing)
    setCurrentStep(5); 
  };

  const isFormValid = income.length > 0;

  return (
    <div 
      className="flex flex-col min-h-[100dvh] w-full max-w-[400px] mx-auto bg-white antialiased text-[#111827]"
      style={{ fontFamily: '"SF Pro", "SF Pro Display", -apple-system, sans-serif' }}
    >
      
      {/* 1. Header & Progress Bar */}
      <div className="pt-6 pb-4 px-6 bg-white flex-none">
        <div className="flex items-center justify-between mb-5">
          <button 
            onClick={() => setCurrentStep(3)} // Back to Home Loan Confirmation
            className="text-gray-800 focus:outline-none"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          
          {/* Progress Bar - Updated to exact brand green */}
          <div className="flex-1 mx-4 flex items-center">
            <div className="h-[7px] flex-1 bg-[#c9f7e0] rounded-full overflow-hidden">
              <div className="h-full bg-[#12F280] w-full"></div> {/* 4/4 Full Width */}
            </div>
          </div>
          <span className="text-xs text-gray-500 font-medium">4/4</span>
        </div>
        
        <h1 className="text-[24px] font-bold tracking-tight mb-1.5 text-gray-900 leading-tight">
          Let’s find your balance transfer options
        </h1>
        <p className="text-gray-500 text-[14px]">
          Just a few details to help us find suitable offers from our lending partners.
        </p>
      </div>

      {/* 2. Form Content */}
      <div className="px-6 flex-1 overflow-y-auto pb-6">
        <div className="flex flex-col gap-6 pt-2">
          
          {/* Occupation Toggle */}
          <div>
            <h2 className="text-[15px] font-semibold text-gray-900 mb-3">What’s your occupation?</h2>
            <div className="flex gap-3">
              {/* Salaried Button */}
              <button
                onClick={() => setOccupation("salaried")}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border ${
                  occupation === "salaried" 
                    ? "border-blue-500 bg-blue-50 text-gray-900" 
                    : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                } transition-colors`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={occupation === "salaried" ? "#3b82f6" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
                <span className="text-[14px] font-medium">Salaried</span>
              </button>

              {/* Self-Employed Button */}
              <button
                onClick={() => setOccupation("self_employed")}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border ${
                  occupation === "self_employed" 
                    ? "border-blue-500 bg-blue-50 text-gray-900" 
                    : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                } transition-colors`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={occupation === "self_employed" ? "#3b82f6" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span className="text-[14px] font-medium">Self-Employed</span>
              </button>
            </div>
          </div>

          {/* Dotted Divider */}
          <div className="border-t border-dashed border-gray-200"></div>

          {/* Monthly Income Input */}
          <div>
            <h2 className="text-[15px] font-semibold text-gray-900 mb-3">What is your monthly net income?</h2>
            <div className="w-full flex flex-col justify-center border border-gray-200 rounded-xl px-4 py-2 focus-within:border-black focus-within:ring-1 focus-within:ring-black transition-all">
              <span className="text-[11px] text-gray-500 font-medium">Monthly Income</span>
              <div className="flex items-center mt-1">
                {income && <span className="text-gray-900 text-[16px] mr-1">₹</span>}
                <input
                  type="text"
                  inputMode="numeric"
                  value={income}
                  onChange={handleIncomeChange}
                  placeholder="Your take-home salary per month"
                  className="w-full bg-transparent outline-none text-[16px] text-gray-900 placeholder:text-gray-400 placeholder:font-normal"
                />
              </div>
            </div>
          </div>

          {/* Loan Requirement Box */}
          <div className="bg-gray-50 rounded-[18px] border border-gray-100 p-4">
            <h2 className="text-[15px] font-semibold text-gray-900 mb-3">What are you looking for?</h2>
            
            <div className="flex flex-col gap-3">
              {/* Transfer Only Option */}
              <button
                onClick={() => setLoanRequirement("transfer_only")}
                className={`flex items-center justify-between px-4 py-3.5 rounded-xl border ${
                  loanRequirement === "transfer_only" 
                    ? "border-blue-500 bg-blue-50" 
                    : "border-gray-200 bg-white hover:bg-gray-50"
                } transition-colors w-full`}
              >
                <div className="flex items-center gap-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                    <path d="M17 2.1l4 4-4 4"/>
                    <path d="M3 12.2v-2a4 4 0 0 1 4-4h13.8"/>
                    <path d="M7 21.9l-4-4 4-4"/>
                    <path d="M21 11.8v2a4 4 0 0 1-4 4H3.2"/>
                  </svg>
                  <span className="text-[14px] font-medium text-gray-700 tracking-tight">Balance Transfer Only</span>
                </div>
                {/* Radio Indicator */}
                <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${loanRequirement === "transfer_only" ? "border-blue-500 bg-blue-500" : "border-gray-300 bg-white"}`}>
                  {loanRequirement === "transfer_only" && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
              </button>

              {/* Transfer + Top-Up Option */}
              <button
                onClick={() => setLoanRequirement("transfer_topup")}
                className={`flex items-center justify-between px-4 py-3.5 rounded-xl border ${
                  loanRequirement === "transfer_topup" 
                    ? "border-blue-500 bg-blue-50" 
                    : "border-gray-200 bg-white hover:bg-gray-50"
                } transition-colors w-full`}
              >
                <div className="flex items-center gap-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
                    <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
                    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
                    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
                  </svg>
                  <span className="text-[14px] font-medium text-gray-900 tracking-tight">Balance Transfer + Top-Up</span>
                </div>
                {/* Radio Indicator */}
                <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${loanRequirement === "transfer_topup" ? "border-blue-500 bg-blue-500" : "border-gray-300 bg-white"}`}>
                  {loanRequirement === "transfer_topup" && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Static Footer (Matches HomeLoanConfirmation) */}
      <div className="bg-white border-t border-gray-100 px-6 pt-4 pb-8 flex-none z-10">
        <button 
          onClick={handleContinue}
          disabled={!isFormValid}
          className="w-full bg-black text-white font-medium py-3.5 rounded-[16px] text-[16px] active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
        >
          Show My Options
        </button>
      </div>
      
    </div>
  );
};

export default BalanceTransferOptions;